import L from 'leaflet';
import { mapImageSize, COORDINATES_MULTIPLIER } from './map/MapConstants';
import Utils from './_Utils';
import { DEBUG_MODE, DebugConfig } from './_DebugConfig';
import { debug } from './_DebugLog';

const DEFAULT_LAT_LNG_TL = L.latLng(35.717271, 139.509985);
const DEFAULT_LAT_LNG_BR = L.latLng(35.715309, 139.515776);

const DEBUG_USER_LOC_X = 492 / 1120;
const DEBUG_USER_LOC_Y = 434 / 480;

const LOG_GEOLOCATION = false;

//const DEFAULT_LAT_LNG_TL = L.latLng(35.664241, 139.754589);
//const DEFAULT_LAT_LNG_BR = L.latLng(DEFAULT_LAT_LNG_TL.lat + (35.715309 - 35.717271), DEFAULT_LAT_LNG_TL.lng + (139.515776 - 139.509985));

export const CHECK_PROXIMITY_ON_INTERVAL = true;

export class UserLocation
{
	constructor()
	{
		this.latLngTL = DEFAULT_LAT_LNG_TL;
		this.latLngBR = DEFAULT_LAT_LNG_BR;

		//Debug mode
		this.debugMode = new URLSearchParams(window.location.search).get('debug-user-loc') === '1';
		this.debugUserGeolocation = null;

		this.map = null;
		this.rasterCoords = null;

		this.updateInterval = 10000;
		//this.updateInterval = 5000;

		this.intervalID = null;
		this.startInterval();

		this.setTargetPin = null;
		this.setTargetPinByXY = null;

		this.highlightPin = null;

		this.onLocationUpdate = null;
		this.onProximityUpdate = null;

		// New properties for caching current position
		this.currentPosition = null;
		this.isUpdatingPosition = false;
	}

	static getInstance()
	{
		if (!this.instance)
		{
			this.instance = new UserLocation();
		}
		return this.instance;
	}

	getCurrentPosition()
	{
		return this.currentPosition;
	}

	isPositionUpdating()
	{
		return this.isUpdatingPosition;
	}

	geolocationToRasterCoords(latitude, longitude)
	{
		const latRange = this.latLngTL.lat - this.latLngBR.lat;
		const lngRange = this.latLngBR.lng - this.latLngTL.lng;
		const x = ((longitude - this.latLngTL.lng) / lngRange) * mapImageSize.w;
		const y = ((this.latLngTL.lat - latitude) / latRange) * mapImageSize.h;
		return [x, y];
	}

	rasterCoordsToGeolocation(x, y, multiplier = COORDINATES_MULTIPLIER)
	{
		const latRange = this.latLngTL.lat - this.latLngBR.lat;
		const lngRange = this.latLngBR.lng - this.latLngTL.lng;
		
		const latitude = this.latLngTL.lat - (y / (mapImageSize.h / multiplier) * latRange);
		const longitude = this.latLngTL.lng + (x / (mapImageSize.w / multiplier) * lngRange);
		
		return { lat: latitude, lng: longitude };
	}

    checkStampItemProximity(stampItem, radius)
    {
        if (!stampItem) return false;

		// If not in DEBUG_MODE, always perform the check
        // If in DEBUG_MODE and GPS check is disabled, skip the check
        if (DEBUG_MODE && !DebugConfig.isGPSCheckEnabled())
		{
			debug.log('GPS check disabled in debug mode - allowing proximity');
			return true;
		}

        // Get target coordinates using the same method as map pins
        const targetLatLng = this.rasterCoordsToGeolocation(
            stampItem.x, 
            stampItem.y,
            COORDINATES_MULTIPLIER
        );

        if (!targetLatLng) return false;

        return this.checkLatLngProximity(targetLatLng, radius);
    }

    checkLatLngProximity(targetLatLng, radius)
    {
        if (!this.currentPosition) return false;

        const { latitude, longitude } = this.currentPosition;

        // Calculate distance using Utils (convert km to meters)
        const distance = Utils.calculateDistance(
            latitude, 
            longitude,
            targetLatLng.lat,
            targetLatLng.lng
        ) * 1000; // Convert km to m

        debug.log(`[Debug: Distance=${distance}m, Radius=${radius}m`);
        
        return distance <= radius;
    }

	handleLocationUpdate(latitude, longitude)
	{
		if (!this.debugUserGeolocation && this.debugMode)
		{
			this.getDebugUserLocation(latitude, longitude);
		}

		// Update cached position
		this.currentPosition = { latitude, longitude };

		if (latitude <= this.latLngTL.lat && latitude >= this.latLngBR.lat && 
			longitude >= this.latLngTL.lng && longitude <= this.latLngBR.lng)
		{
			const rasterCoords = this.geolocationToRasterCoords(latitude, longitude);
			if (this.onLocationUpdate) this.onLocationUpdate(rasterCoords[0], rasterCoords[1]);
		}
	}

	updateUserLocation()
	{
		if (LOG_GEOLOCATION) console.log('[Updating user location...]');

		this.isUpdatingPosition = true;

		navigator.geolocation.getCurrentPosition(
			(position) =>
			{
				const { latitude, longitude } = position.coords;
				if (LOG_GEOLOCATION) console.log(`[Current geolocation is ${latitude}, ${longitude}]`);
				this.handleLocationUpdate(latitude, longitude);
				
				// Add proximity check on interval
				if (CHECK_PROXIMITY_ON_INTERVAL && this.onProximityUpdate)
				{
					this.onProximityUpdate();
				}
				
				this.isUpdatingPosition = false;
			},
			(err) => 
			{
				console.error(err);
				this.isUpdatingPosition = false;
			},
			{ 
				enableHighAccuracy: true 
			}
		);

		// Reset the interval
		this.startInterval();
	}

	startInterval()
	{
		clearInterval(this.intervalID);
		this.intervalID = setInterval(() => this.updateUserLocation(), this.updateInterval);
	}

	setUpdateInterval(interval)
	{
		this.updateInterval = interval;
		this.startInterval();
		console.log(`[update interval changed to ${interval}]`);
	}

	setLatLngTL(lat = this.latLngTL.lat, lng = this.latLngTL.lng)
	{
		this.latLngTL = L.latLng(lat, lng);
		console.log(`[latlngTL changed to ${lat}, ${lng}]`);
	}

	setLatLngBR(lat = this.latLngBR.lat, lng = this.latLngBR.lng)
	{
		this.latLngBR = L.latLng(lat, lng);
		console.log(`[latlngTL changed to ${lat}, ${lng}]`);
	}

	resetLatLngBounds()
	{
		this.latLngTL = DEFAULT_LAT_LNG_TL;
		this.latLngBR = DEFAULT_LAT_LNG_BR;
		console.log(`[latlng bounts resetted]`)
	}

	requestLocationPermission()
	{
		navigator.permissions.query({ name: 'geolocation' }).then((result) =>
		{
			if (result.state === 'prompt')
			{
				navigator.geolocation.getCurrentPosition(() => { }, () => { }, {});
			}
		});
	}

	xyToLatLng(x, y, multiplier = 1)
	{
		if (!this.rasterCoords) return null;

		return this.rasterCoords.unproject([x * multiplier, y * multiplier]);
	}

	setViewByXY(x, y, multiplier = 1)
	{
		if (!this.map || !this.rasterCoords) return;

		const latLng = this.xyToLatLng(x, y, multiplier);

		this.map.setView(latLng);
	}

	getDebugUserLocation(latitude, longitude)
	{
		this.debugUserGeolocation = L.latLng(latitude, longitude);

		const distLat = this.latLngBR.lat - this.latLngTL.lat;
		const distLng = this.latLngBR.lng - this.latLngTL.lng;

		const difLat = distLat * DEBUG_USER_LOC_Y;
		const difLng = distLng * DEBUG_USER_LOC_X;

		// Adjust latLngTL and latLngBR based on the debugUserGeolocation
		this.latLngTL = L.latLng(this.debugUserGeolocation.lat - difLat, this.debugUserGeolocation.lng - difLng);
		this.latLngBR = L.latLng(this.latLngTL.lat + distLat, this.latLngTL.lng + distLng);
	}

	setProximityUpdateCallback(callback)
	{
		this.onProximityUpdate = callback;
	}
}